import ExampleTheme from "./themes/ExampleTheme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";

import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";

import "./styles.css";
import { $generateHtmlFromNodes } from "@lexical/html";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { $getRoot, COPY_COMMAND, createEditor } from "lexical";
import { LoadInitialContent } from "./plugins/LoadInitialContent";
import { useContext, useEffect } from "react";
import {
  LexicalComposerContext,
  useLexicalComposerContext,
} from "@lexical/react/LexicalComposerContext";
import { ClearEditorContents } from "./plugins/ClearEditorContents";

function Placeholder({ placeholder }) {
  return (
    <div className="editor-placeholder">
      {placeholder ?? "Enter your text..."}
    </div>
  );
}

const editorConfig = {
  // The editor theme
  theme: ExampleTheme,
  // Handling of errors during update
  onError(error) {
    throw error;
  },
  // Any custom nodes go here
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
  ],
};

function onChange(editorState, editor, handleContentChange) {
  let htmlString = "";
  editorState.read(() => {
    htmlString = $generateHtmlFromNodes(editor, null);
  });

  const jsonStringify = JSON.stringify(editorState);

  const combined = {
    json: jsonStringify,
    html: htmlString,
  };
  //stringify the combined object
  const combinedString = JSON.stringify(combined);
  handleContentChange(combinedString);
}

export default function RichTextEditor({
  handleContentChange,
  placeholder,
  initialValue,
  receivedContent,
  isClearEditor,
  setIsClearEditor,
  autoFocus,
}) {
  return (
    <LexicalComposer initialConfig={editorConfig}>
      <ClearEditorContents
        isClearEditor={isClearEditor}
        setIsClearEditor={setIsClearEditor}
      />
      <LoadInitialContent
        initialContent={initialValue}
        receivedContent={receivedContent}
      />
      <div className="editor-container">
        <ToolbarPlugin />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            // if initialValue is empty show placeholder otherwise null
            placeholder={
              initialValue ? null : <Placeholder placeholder={placeholder} />
            }
            ErrorBoundary={({ error }) => (
              <div className="editor-error">{error.message}</div>
            )}
          />
          <OnChangePlugin
            onChange={(editorState, editor) =>
              onChange(editorState, editor, handleContentChange)
            }
          />
          <HistoryPlugin />
          {/*<TreeViewPlugin />*/}
          {autoFocus && <AutoFocusPlugin />}

          <CodeHighlightPlugin />
          <ListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          {/*<MarkdownShortcutPlugin transformers={TRANSFORMERS} />*/}
        </div>
      </div>
    </LexicalComposer>
  );
}
